import { useEffect, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';

import { BusinessSwitcher } from './BusinessSwitcher.tsx';
import { Button } from './ui/index.tsx';
import { VerifyBanner } from './VerifyBanner.tsx';
import { useSession } from '../state/session.tsx';
import { useTheme } from '../state/theme.tsx';
import './shell.css';

interface NavItem {
  to: string;
  label: string;
  glyph: string;
  end?: boolean;
}

const SELLING: NavItem[] = [
  { to: '/', label: 'Overview', glyph: '◴', end: true },
  { to: '/invoices', label: 'Invoices', glyph: '▤' },
  { to: '/quotations', label: 'Quotations', glyph: '◫' },
  { to: '/proformas', label: 'Proformas', glyph: '◨' },
  { to: '/credit-notes', label: 'Credit notes', glyph: '◧' },
];

const OPERATIONS: NavItem[] = [
  { to: '/purchase-orders', label: 'Purchase orders', glyph: '◩' },
  { to: '/delivery-challans', label: 'Delivery challans', glyph: '◪' },
  { to: '/payments', label: 'Payments', glyph: '⇄' },
  { to: '/expenses', label: 'Expenses', glyph: '⊟' },
];

const RECORDS: NavItem[] = [
  { to: '/customers', label: 'Customers', glyph: '◍' },
  { to: '/vendors', label: 'Vendors', glyph: '◌' },
  { to: '/items', label: 'Items', glyph: '◇' },
  { to: '/reports', label: 'Reports', glyph: '◈' },
];

const THEME_GLYPHS: Record<string, string> = { system: '◐', light: '☀', dark: '☾' };

export function AppShell() {
  const { user, signOut } = useSession();
  const { preference, cycle } = useTheme();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Navigating on a phone should close the drawer behind you.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <div className={`shell${menuOpen ? ' shell--menu-open' : ''}`}>
      <a className="skip-link" href="#main">
        Skip to content
      </a>

      <div className="shell__scrim" onClick={() => setMenuOpen(false)} aria-hidden="true" />

      <aside className={`sidebar no-print${menuOpen ? ' sidebar--open' : ''}`}>
        <div className="sidebar__brand">
          <span className="sidebar__wordmark">Vela</span>
          <span className="sidebar__dot" aria-hidden="true" />
          <span className="sidebar__byline">by Atronova</span>
        </div>

        <BusinessSwitcher />

        <nav className="sidebar__nav" aria-label="Main">
          <NavGroup items={SELLING} />
          <p className="eyebrow sidebar__section">Operations</p>
          <NavGroup items={OPERATIONS} />
          <p className="eyebrow sidebar__section">Records</p>
          <NavGroup items={RECORDS} />
        </nav>

        <div className="sidebar__footer stack stack-2">
          <NavLink
            to="/settings"
            className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}
          >
            <span className="navlink__glyph" aria-hidden="true">
              ⚙
            </span>
            Settings
          </NavLink>
          <div className="row">
            <span className="truncate faint" style={{ fontSize: 'var(--text-xs)' }}>
              {user?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="push"
              onClick={() => void signOut()}
              title="Sign out"
            >
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar no-print">
          <Button
            variant="ghost"
            size="sm"
            className="topbar__menu"
            onClick={() => setMenuOpen((value) => !value)}
            aria-label="Toggle navigation"
            aria-expanded={menuOpen}
          >
            ☰
          </Button>
          <div className="grow" />
          <Button
            variant="ghost"
            size="sm"
            onClick={cycle}
            title={`Theme: ${preference}`}
            aria-label={`Theme: ${preference}. Click to change.`}
          >
            {THEME_GLYPHS[preference]}
          </Button>
        </header>

        <VerifyBanner />

        <main className="content" id="main">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavGroup({ items }: { items: NavItem[] }) {
  return (
    <>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) => `navlink${isActive ? ' navlink--active' : ''}`}
        >
          <span className="navlink__glyph" aria-hidden="true">
            {item.glyph}
          </span>
          {item.label}
        </NavLink>
      ))}
    </>
  );
}
